import React from 'react';
import styled from 'styled-components';

// Main container with max width
export const Container = styled.div`
  width: 100%;
  max-width: ${({ theme, size }) => 
    size === 'sm' ? theme.grid.containerWidth.sm :
    size === 'md' ? theme.grid.containerWidth.md :
    size === 'lg' ? theme.grid.containerWidth.lg :
    size === 'xxl' ? theme.grid.containerWidth.xxl :
    theme.grid.containerWidth.xl};
  margin-left: auto;
  margin-right: auto;
  padding-left: ${({ theme }) => theme.spacing[4]};
  padding-right: ${({ theme }) => theme.spacing[4]};
  
  @media (min-width: 768px) {
    padding-left: ${({ theme }) => theme.spacing[6]};
    padding-right: ${({ theme }) => theme.spacing[6]};
  }
`;

// Page section with consistent padding
export const Section = styled.section`
  padding: ${({ theme, padding }) => 
    padding === 'sm' ? `${theme.spacing[4]} 0` :
    padding === 'lg' ? `${theme.spacing[12]} 0` :
    padding === 'none' ? '0' :
    `${theme.spacing[8]} 0`};
  
  background-color: ${({ theme, background }) => 
    background === 'light' ? theme.colors.light :
    background === 'dark' ? theme.colors.dark :
    background === 'primary' ? theme.colors.primary :
    background === 'secondary' ? theme.colors.secondary :
    background || 'transparent'};
    
  color: ${({ theme, background }) => 
    background === 'dark' || background === 'primary' || background === 'secondary' ? 
    theme.colors.white : 
    theme.colors.dark};
`;

// Grid system
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns || 12}, 1fr);
  gap: ${({ theme, gap }) => gap ? theme.spacing[gap] : theme.spacing[4]};
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(${({ mobileColumns }) => mobileColumns || 1}, 1fr);
  }
`;

// Grid item
export const GridItem = styled.div`
  grid-column: span ${({ span }) => span || 12};
  
  @media (max-width: 768px) {
    grid-column: span ${({ mobileSpan }) => mobileSpan || 12};
  }
`;

// Flex container
export const Flex = styled.div`
  display: flex;
  flex-direction: ${({ direction }) => direction || 'row'};
  justify-content: ${({ justify }) => justify || 'flex-start'};
  align-items: ${({ align }) => align || 'stretch'};
  flex-wrap: ${({ wrap }) => wrap || 'nowrap'};
  gap: ${({ theme, gap }) => gap ? theme.spacing[gap] : '0'};
  
  @media (max-width: 768px) {
    flex-direction: ${({ mobileDirection }) => mobileDirection || 'column'};
  }
`;

// Page wrapper
export const PageWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  min-height: calc(100vh - 180px); // Adjust based on header/footer height
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

// Page header
export const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

// Page title
export const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  color: ${({ theme }) => theme.colors.dark};
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
`;

// Page description
export const PageDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.medium};
  margin: 0;
`;

// Divider
export const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.border};
  margin: ${({ theme, spacing }) => spacing ? `${theme.spacing[spacing]} 0` : `${theme.spacing[4]} 0`};
`;

// Empty state component
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${({ theme }) => theme.spacing[8]};
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  
  svg {
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    color: ${({ theme }) => theme.colors.medium};
  }
`;

export const EmptyStateTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  color: ${({ theme }) => theme.colors.dark};
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
`;

export const EmptyStateDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.medium};
  margin: 0 0 ${({ theme }) => theme.spacing[4]};
  max-width: 500px;
`;

// Layout component
const Layout = ({ children }) => <>{children}</>;

export default Layout;